import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Toast } from "@sscs/ui";
import {
  SETTINGS_ROUTE,
  USER_DIRECTORY_ROUTE,
  VERIFY_SSO_CHANGES_ROUTE,
} from "Constants";
import { useLoginInfo } from "Hooks";
import { VerificationStatus } from "Types";
import {
  apiErrorToast,
  buildOidcAuthorizationUrl,
  getBaseRoute,
  parseOidcStateParam,
} from "Utils";
import {
  useApplyOidcConfigMutation,
  useApplySamlConfigMutation,
} from "../services/sso.api";

type UseVerifySSOChanges = {
  verificationStatus: VerificationStatus;
  onClose: () => void;
};

const useVerifySSOChanges = (): UseVerifySSOChanges => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [applyOidcConfig] = useApplyOidcConfigMutation();
  const [applySamlConfig] = useApplySamlConfigMutation();
  const { hasLoaded, ssoInfo, isSSOLogin, refetchLoginInfo } = useLoginInfo();

  const handleOnClose = useCallback(() => {
    if (isSSOLogin && ssoInfo?.sso_type === "sso-oidc") {
      window.location.href = buildOidcAuthorizationUrl({
        authorizationUrl: ssoInfo.oidc_authorization_url,
        clientId: ssoInfo.oidc_client_id,
        responseType: ssoInfo.oidc_response_type,
        scopes: ssoInfo.oidc_scopes,
        redirectUri: `${getBaseRoute()}/${SETTINGS_ROUTE}/${USER_DIRECTORY_ROUTE}`,
        state: ssoInfo.oidc_state,
        nonce: ssoInfo.oidc_nonce,
      });

      return;
    }

    navigate(`/${SETTINGS_ROUTE}/${USER_DIRECTORY_ROUTE}`, { replace: true });
  }, [isSSOLogin, navigate, ssoInfo]);

  const authorizationCode = searchParams.get("code");
  const isSamlCallback = authorizationCode === "saml";
  const { state: oidcState, payload: oidcConfigId } = parseOidcStateParam(
    searchParams.get("state"),
  );
  const updatedConfigId = isSamlCallback
    ? searchParams.get("config_id")
    : oidcConfigId;

  // there are some instances where the IDP will send back an error so we can show it to the user
  const error = searchParams.get("error");
  const errorDescription = searchParams.get("error_description");
  const hasIdpError = Boolean(error && errorDescription);

  const [verificationStatus, setVerificationStatus] =
    useState<VerificationStatus>("status-unknown");

  // the OIDC provider will send back an error and description in some cases
  // i.e. when you miss 'openid' from the list of scopes
  useEffect(() => {
    if (error && errorDescription) {
      setVerificationStatus("status-error");

      Toast.show({
        title: errorDescription,
        type: "error",
      });
    }
  }, [error, errorDescription]);

  // once changes are successfully applied, we need to refetch the login info to get the new values to the RTKQuery cache
  // this is so when the user clicks the OK button on the VerifySSOChangesLayout page, we can forward them to the correct URL
  useEffect(() => {
    if (verificationStatus === "status-verified") refetchLoginInfo();
  }, [verificationStatus, refetchLoginInfo]);

  useEffect(() => {
    if (!hasIdpError && (!authorizationCode || !updatedConfigId)) {
      navigate("/", { replace: true });
    }
  }, [authorizationCode, hasIdpError, navigate, updatedConfigId]);

  useEffect(() => {
    const verifyOIDC = async () => {
      if (isSamlCallback && updatedConfigId) {
        try {
          await applySamlConfig({ configId: updatedConfigId }).unwrap();
          setVerificationStatus("status-verified");
        } catch (samlError) {
          setVerificationStatus("status-error");
          apiErrorToast(
            samlError,
            "Unable to verify SSO configuration changes",
          );
        }
      } else if (authorizationCode && updatedConfigId && oidcState) {
        try {
          await applyOidcConfig({
            oidcSsoLoginBody: {
              authorization_code: authorizationCode,
              redirect_uri: `${getBaseRoute()}/${VERIFY_SSO_CHANGES_ROUTE}`,
              state: oidcState,
            },
            configId: updatedConfigId,
          }).unwrap();

          setVerificationStatus("status-verified");
        } catch (oidcError) {
          setVerificationStatus("status-error");
          apiErrorToast(
            oidcError,
            "Unable to verify SSO configuration changes",
          );
        }
      } else {
        setVerificationStatus("status-error");
      }
    };

    if (hasLoaded) verifyOIDC();
  }, [
    applyOidcConfig,
    applySamlConfig,
    authorizationCode,
    hasLoaded,
    isSamlCallback,
    oidcState,
    updatedConfigId,
  ]);

  return {
    verificationStatus,
    onClose: handleOnClose,
  };
};
export default useVerifySSOChanges;
